/*
 * Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import useStyles from "./style";
import CloseIcon from '@material-ui/icons/Close';
import { PreLoaderLogo } from "../../../../elements/PreLoaderLogo";
import GreyButton from "../../../../elements/GreyButton";
import PrimaryButton from "../../../../elements/PrimaryButton";
import { ApiKeysReducer, ApplicationState } from "../../../../data/interfaces";
import { connect } from "react-redux";
import { regenerateApiKeyV2 } from "../../../../data/operations";
import ApiKeyView from "../ApiKeyView";
import { useIntl } from "react-intl";
import LoadingLogo from "./LoadingLogo";

interface APIKeyRegenerationConfirmation {
    open: boolean;
    apiKeysReducer: ApiKeysReducer;
    selectedApiKey: string;

    regenerateApiKey: (apiKeyId: string) => void,
    onClose: () => void;
}

function APIKeyRegenerationConfirmation(props: APIKeyRegenerationConfirmation) {
    const classes = useStyles();
    const intl = useIntl();
    const { open, apiKeysReducer, selectedApiKey, onClose, regenerateApiKey } = props;

    const [isLoading, setIsLoading] = useState(false);

    const [isRegenerationInitiated, setIsRegenerationInitiated] = React.useState(false);

    const [showRegeneratedApiKey, setShowRegeneratedApiKey] = React.useState(false);

    const confirmRegenerateApiKey = () => {
        regenerateApiKey(selectedApiKey);
        setIsRegenerationInitiated(true);
        setIsLoading(true);
    }

    const closeRegenerateDialog = () => {
        onClose();
        setShowRegeneratedApiKey(false);
        setIsRegenerationInitiated(false);
        setIsLoading(false);
    }

    useEffect(() => {
        if (isRegenerationInitiated && !apiKeysReducer.isKeyRegenerating && apiKeysReducer.regenerateApiKeyerror === '') {
            setShowRegeneratedApiKey(true);
            setIsRegenerationInitiated(false);
            setIsLoading(false);
        } else if (isRegenerationInitiated && !apiKeysReducer.isKeyRegenerating && apiKeysReducer.regenerateApiKeyerror !== '') {
            onClose();
            setIsRegenerationInitiated(false);
            setIsLoading(false);
        }
    }, [apiKeysReducer.isKeyRegenerating, apiKeysReducer.regeneratedApiKey]);

    const ConfirmationForm = () => {
        return (
            <Box className={classes.formContainer}>
                <Typography className={classes.confirmationText}>
                    {intl.formatMessage({
                        id: 'apikeys.regenerate.confirmation.text1',
                        defaultMessage: "Regenerating the API key will impact applications using the current key."
                    })}{' '}
                </Typography>
                <Typography>
                    {intl.formatMessage({
                        id: 'apikeys.regenerate.confirmation.text2',
                        defaultMessage: "The current key will"
                    })}{' '}
                    <strong>
                        {intl.formatMessage({
                            id: 'apikeys.regenerate.confirmation.text3.bold',
                            defaultMessage: "expire in 1 hour."
                        })}
                    </strong>{' '}
                    {intl.formatMessage({
                        id: 'apikeys.regenerate.confirmation.text4',
                        defaultMessage: "To avoid service disruptions, copy the new key and update it in your applications immediately."
                    })}
                </Typography>
            </Box>
        );
    };

    const ActionFooter = () => {
        return (
            <Box pb={2} mt={3} flexDirection="row" justifyContent="flex-end" display="flex">
                <GreyButton
                    onClick={closeRegenerateDialog}
                    className={classes.cancelBtn}
                    buttonLabel={"Cancel"}
                />
                <PrimaryButton
                    onClick={confirmRegenerateApiKey}
                    buttonLabel={"Regenerate"}
                    className={classes.regenerateBtn}
                    id="create-button"
                />
            </Box>
        );
    }

    return (
        <React.Fragment>
            <Dialog
                    open={open}
                    onClose={(_,reason) => {
                        if (reason !== 'backdropClick') {
                            closeRegenerateDialog()
                        }
                    }}
                    aria-labelledby="delete-app-dialog-title"
                    fullWidth={!isLoading}
                    classes={{ paper: classes.customWidth }}
                >
                    <DialogTitle className={classes.dialogTitle}>
                        { !isLoading &&
                            <Typography className={classes.primaryTitle}>
                                Regenerate API Key
                            </Typography>
                        }
                        {
                            !isLoading ? (
                                <IconButton className={classes.closeButton} onClick={closeRegenerateDialog}>
                                    <CloseIcon />
                                </IconButton>
                            ) : null
                        }
                    </DialogTitle>
                    <DialogContent classes={{ root: classes.createApiDialogContent }}>
                        {  isLoading && <LoadingLogo />}
                        { !isLoading && !showRegeneratedApiKey && <ConfirmationForm />}
                        { !isLoading && !showRegeneratedApiKey && <ActionFooter />}
                        { !isLoading && showRegeneratedApiKey && <ApiKeyView 
                            generatedApiKey={apiKeysReducer.regeneratedApiKey} isInProgress={isLoading} onClose={closeRegenerateDialog} />}
                    </DialogContent>
                </Dialog>
        </React.Fragment>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiKeysReducer: state.apiKeysReducer,
    }
}

const mapDispatchToProps = {
    regenerateApiKey: regenerateApiKeyV2,
};

export default connect(mapStateToProps, mapDispatchToProps)(APIKeyRegenerationConfirmation);
